import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { siteInfo, testimonials } from "@/mocks/novels";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem("phone_alt") as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setFormStatus("success");
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      const res = await fetch(
        "https://readdy.ai/api/form/d9sqqv58o1vn02ekukkg",
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
        }
      );

      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore
      }

      if (res.ok && (parsed as { code?: string })?.code === "OK") {
        setFormStatus("success");
        form.reset();
        setFormError("");
      } else {
        const serverMsg =
          (parsed as { meta?: { message?: string } })?.meta?.message ||
          (parsed as { message?: string })?.message ||
          (parsed as { meta?: { detail?: string } })?.meta?.detail ||
          responseText;
        if (serverMsg.includes("spam")) {
          setFormStatus("error");
          setFormError("提交失败，请稍后再试");
        } else {
          setFormStatus("error");
          setFormError(serverMsg || "提交失败，请稍后再试");
        }
      }
    } catch {
      setFormStatus("error");
      setFormError("网络错误，请检查连接后重试");
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <Breadcrumb />

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              关于 po18脸红心跳
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              {siteInfo.description}
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100/70">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8">
              <div className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center">
                  <i className="ri-book-read-line text-3xl text-primary-500"></i>
                </div>
                <p className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">
                  {siteInfo.novelCount}
                </p>
                <p className="text-sm text-foreground-500">精选小说</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center">
                  <i className="ri-user-heart-line text-3xl text-primary-500"></i>
                </div>
                <p className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">
                  {siteInfo.readerCount}
                </p>
                <p className="text-sm text-foreground-500">忠实读者</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center">
                  <i className="ri-chat-smile-2-line text-3xl text-primary-500"></i>
                </div>
                <p className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">
                  {siteInfo.reviewCount}
                </p>
                <p className="text-sm text-foreground-500">读者好评</p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-2">
              我们的故事
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              始于热爱，忠于品质
            </p>

            <div className="max-w-3xl mx-auto space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center mt-0.5">
                  <span className="text-sm font-bold text-primary-600">1</span>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground-800 mb-1">
                    始于{siteInfo.established}年
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    po18脸红心跳诞生于{siteInfo.established}年，由一群热爱言情小说的资深读者创建。最初的灵感来自于一个简单的想法：为喜欢言情小说的读者打造一个真正懂她们口味的推荐平台。我们不追求数量，只推荐真正值得一读的好故事。
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center mt-0.5">
                  <span className="text-sm font-bold text-primary-600">2</span>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground-800 mb-1">
                    专业编辑团队
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    我们的编辑团队由资深言情小说读者和文学专业背景的成员组成。每一篇推荐都经过至少两轮筛选和审核，确保推荐内容既有深度又有温度。从甜宠到虐恋，从古言到穿书，我们覆盖所有热门分类。
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center mt-0.5">
                  <span className="text-sm font-bold text-primary-600">3</span>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground-800 mb-1">
                    社群驱动
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    我们相信最好的推荐来自读者。po18脸红心跳建立了活跃的读者社群，读者可以在每本书的评论区和FAQ中交流心得。平台会根据读者反馈持续优化推荐算法，让好故事被更多人看到。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100/70">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-2">
              读者口碑
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              来自{siteInfo.readerCount}位读者的真实评价
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {testimonials.map((t, idx) => (
                <div
                  key={idx}
                  className="p-5 rounded-lg bg-background-50 border border-background-200/70"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-800">
                        {t.name}
                      </p>
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: t.rating }).map((_, i) => (
                          <i
                            key={i}
                            className="ri-star-fill text-accent-500 text-xs"
                          ></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    &ldquo;{t.text}&rdquo;
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-2">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              有任何建议或合作意向，欢迎联系我们
            </p>

            {formStatus === "success" ? (
              <div className="p-6 bg-accent-100 text-accent-800 rounded-lg text-center text-sm">
                感谢你的留言！我们会尽快回复你。
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form=""
                className="space-y-4"
              >
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="website-alt-field"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      姓名
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="你的名字"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="subject"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    主题
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="留言主题"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    留言内容
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入你的留言（500字以内）"
                    required
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">
                    最多500个字符
                  </p>
                </div>
                {formError && (
                  <p className="text-xs text-red-500">{formError}</p>
                )}
                <button
                  type="submit"
                  className="w-full py-3 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-lg text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  发送留言
                </button>
              </form>
            )}

            <div className="mt-8 p-6 rounded-lg bg-background-100 border border-background-200/70">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div>
                  <p className="text-sm font-medium text-foreground-800">
                    邮箱联系
                  </p>
                  <p className="text-sm text-foreground-500">
                    {siteInfo.email}
                  </p>
                </div>
                <div className="hidden sm:block w-px h-10 bg-background-300"></div>
                <div>
                  <p className="text-sm font-medium text-foreground-800">
                    工作时间
                  </p>
                  <p className="text-sm text-foreground-500">
                    周一至周五 9:00 - 18:00
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}